/**
 * PlaybackPreferenceToggle — single-logo header pill showing the currently-
 * preferred platform (Spotify / YouTube / Apple Music) with a small green "ON"
 * badge. Click cycles to the next: Spotify → YouTube → Apple Music → Spotify.
 *
 * Single ~50px pill (not 3 buttons) to keep the header on one row. White
 * monochrome glyph + the green "ON" dot marks the active surface. Apple Music
 * uses the lucide note glyph as a stand-in until a brand icon asset lands.
 *
 * Click → cycle + tracks `playback_preference_change` analytics.
 *
 * Andy 2026-05-09: "show just one logo and ON right of it with green."
 * Andy 2026-06-04: cycle in Apple Music as a third option.
 */

import { memo } from "react";
import { AppleMusicIcon } from "@/components/icons/AppleMusicIcon";
import { Assets } from "@/lib/assets";
import { cn } from "@/lib/utils";
import { usePlaybackPreference } from "@/hooks/usePlaybackPreference";
import { APPLE_MUSIC_ENABLED, type PlaybackPreference } from "@/lib/playbackPreference";
import { trackEvent } from "@/lib/analytics";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const LABEL: Record<PlaybackPreference, string> = {
  spotify: "Spotify",
  youtube: "YouTube",
  apple_music: "Apple Music",
};
const NEXT: Record<PlaybackPreference, PlaybackPreference> = APPLE_MUSIC_ENABLED
  ? { spotify: "youtube", youtube: "apple_music", apple_music: "spotify" }
  : { spotify: "youtube", youtube: "spotify", apple_music: "spotify" };

export const PlaybackPreferenceToggle = memo(function PlaybackPreferenceToggle({
  className,
}: {
  className?: string;
}) {
  const { preference, setPreference } = usePlaybackPreference();

  const next = NEXT[preference];
  const currentLabel = LABEL[preference];
  const nextLabel = LABEL[next];

  const handleClick = () => {
    setPreference(next);
    trackEvent("playback_preference_change", "engagement", next);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleClick}
            aria-label={`Primary playback: ${currentLabel}. Click to cycle to ${nextLabel}.`}
            className={cn(
              // tap-target: this is a raw <button>, so it does not inherit the
              // 44px coarse-pointer hit region buttonVariants adds. 28px tall
              // otherwise (design-rules §10).
              "group relative tap-target inline-flex items-center gap-1.5 rounded-full",
              "h-7 px-1.5 py-1",
              "border border-border/40 bg-background/40 hover:bg-background/70",
              "transition-colors duration-200",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
              className,
            )}
          >
            {/* White monochrome glyph - the green ON dot carries the "active"
                semantic. Apple Music uses the lucide note (stand-in for a brand
                icon asset); Spotify/YouTube use their white logo bitmaps. */}
            {preference === "apple_music" ? (
              <AppleMusicIcon className="h-5 w-5 shrink-0 text-white" />
            ) : (
              <img
                src={preference === "spotify" ? Assets.spotify.iconWhite : Assets.youtube.iconWhite}
                alt=""
                width={preference === "spotify" ? 20 : 26}
                height={20}
                className={cn(
                  "object-contain shrink-0 h-5",
                  preference === "spotify" ? "w-5" : "w-[26px]",
                )}
                draggable={false}
              />
            )}
            {/* Green "ON" dot - power-LED metaphor; always emerald (indicates
                "lit/on", not platform identity which the glyph handles). */}
            <span
              aria-hidden="true"
              className={cn(
                "h-1.5 w-1.5 rounded-full bg-success",
                "shadow-[0_0_6px_2px_hsl(142_76%_52%/0.55)]",
              )}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {currentLabel} is primary · click to cycle to {nextLabel}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
